// app/javascript/controllers/session_controller.js
import { Controller } from "@hotwired/stimulus"

// Warns before the session expires, then navigates so a stale tab stops looking
// authenticated.
//
// The navigation is deliberately the only "logout" action: the server decides
// whether the session is actually gone. If it is, Devise redirects to sign-in;
// if a background request happened to keep it alive, the page simply reloads and
// the timer resyncs. That keeps the client from ever guessing wrong.
export default class extends Controller {
  static targets = ["dialog", "countdown"]

  static values = {
    // Inactivity window (Devise config.timeout_in), in ms.
    idleTimeout: Number,
    // Absolute expiry as epoch ms; 0 when uncapped.
    absoluteExpiresAt: Number,
    // How long before expiry to show the warning, in ms.
    warningLead: { type: Number, default: 60000 }
  }

  static ACTIVITY_EVENTS = ["mousedown", "keydown", "scroll", "touchstart", "focus"]

  connect() {
    this.lastActivityAt = Date.now()
    this.navigating = false

    this.recordActivity = this.recordActivity.bind(this)
    this.handleResponse = this.handleResponse.bind(this)
    this.hideWarning = this.hideWarning.bind(this)

    this.constructor.ACTIVITY_EVENTS.forEach((name) => {
      window.addEventListener(name, this.recordActivity, { passive: true, capture: true })
    })
    document.addEventListener("turbo:before-fetch-response", this.handleResponse)
    // Keep the warning out of Turbo's cached snapshot, or it flashes on preview.
    document.addEventListener("turbo:before-cache", this.hideWarning)

    // A wall-clock tick rather than setTimeout, so suspending the machine can't
    // let an expired session sit on screen looking valid.
    this.tick()
    this.timer = setInterval(() => this.tick(), 1000)
  }

  disconnect() {
    this.constructor.ACTIVITY_EVENTS.forEach((name) => {
      window.removeEventListener(name, this.recordActivity, { capture: true })
    })
    document.removeEventListener("turbo:before-fetch-response", this.handleResponse)
    document.removeEventListener("turbo:before-cache", this.hideWarning)
    clearInterval(this.timer)
  }

  recordActivity() {
    this.lastActivityAt = Date.now()
  }

  // Whichever limit bites first wins.
  msUntilExpiry() {
    const limits = []

    if (this.idleTimeoutValue > 0) {
      limits.push(this.lastActivityAt + this.idleTimeoutValue - Date.now())
    }
    if (this.absoluteExpiresAtValue > 0) {
      limits.push(this.absoluteExpiresAtValue - Date.now())
    }

    return limits.length ? Math.min(...limits) : Infinity
  }

  tick() {
    if (this.navigating) return

    const remaining = this.msUntilExpiry()

    if (remaining <= 0) {
      this.expire()
    } else if (remaining <= this.warningLeadValue) {
      this.showWarning(remaining)
    } else {
      this.hideWarning()
    }
  }

  showWarning(remaining) {
    if (this.hasCountdownTarget) {
      const seconds = Math.max(0, Math.ceil(remaining / 1000))
      this.countdownTarget.textContent = `${seconds} second${seconds === 1 ? "" : "s"}`
    }
    if (this.hasDialogTarget) {
      this.dialogTarget.classList.remove("hidden")
    }
  }

  hideWarning() {
    if (this.hasDialogTarget) {
      this.dialogTarget.classList.add("hidden")
    }
  }

  // "Stay signed in" — a real request, which is what resets the server's
  // inactivity clock. Does nothing for the absolute cap, by design.
  extend(event) {
    if (event) event.preventDefault()
    this.recordActivity()
    this.hideWarning()
    this.visit()
  }

  expire() {
    this.hideWarning()
    this.visit()
  }

  visit() {
    if (this.navigating) return
    this.navigating = true
    clearInterval(this.timer)

    if (window.Turbo) {
      window.Turbo.visit(window.location.href, { action: "replace" })
    } else {
      window.location.reload()
    }
  }

  // Catch expiry surfaced by a Turbo request rather than by our own timer.
  handleResponse(event) {
    if (event.detail.fetchResponse.response.status === 401) {
      event.preventDefault()
      window.location.href = "/users/sign_in"
    }
  }
}
